/**
 * @file 右列溢出 tips 虚拟列表行组件。
 * 由 `NewRegionSelect` 在 `enableExactOverflowBubble === true` 时渲染二级 market / 图表行。
 */
import React, { useRef, useMemo } from 'react';
import classNames from 'classnames';
import { Checkbox } from 'tea-component';
import useIsTextOverflow from '@gp/components/EllipsisHover/_hooks/useIsTextOverflow';
import MixedTypeIcon from '@/pages/home/components/MixedTypeIcon';
import CommonBubble from '../CommonBubble';
import type { IExactOverflowMarketItemProps } from './type';
import { resolveListBubbleContent } from './util';

/**
 * 右列虚拟列表行：仅文本溢出时展示 Bubble tips。
 *
 * `Checkbox` 宽度 100% 以保证虚拟列表 flex 链路上文本 span 可正确测量溢出。
 *
 * @remarks
 * - 分组标题行（`isGroupHeader`）由父组件 `MarketGroupHeaderItem` 单独渲染，不走本组件
 * - `listBubbleProps` 来自 `overflowBubbleProps.listRow`
 */
const ExactOverflowMarketItem: React.FC<IExactOverflowMarketItemProps> = ({
  item,
  locale,
  title,
  rawText,
  isNotRegionIcon,
  imgInvert,
  rightBubblePlacement,
  rightBubblePlacementOffset,
  listBubbleProps,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const isOverflow = useIsTextOverflow(textRef, { text: rawText });
  const bubbleContent = useMemo(
    () => resolveListBubbleContent(item, locale, isOverflow, rawText),
    [item, locale, isOverflow, rawText],
  );
  const { value, icon, svgIconType, render, disabled } = item || {};

  return (
    <CommonBubble
      placement={rightBubblePlacement}
      placementOffset={rightBubblePlacementOffset}
      dark
      arrowPointAtCenter
      closeOnScroll
      {...listBubbleProps}
      content={bubbleContent}
    >
      <Checkbox key={value} name={value} disabled={disabled} style={{ width: '100%' }}>
        {render || (
          <>
            <MixedTypeIcon
              icon={icon}
              svgIconType={svgIconType}
              rawText={rawText}
              imgInvert={imgInvert}
              className={classNames('dbc-region-select-item-icon', {
                'device-icon': isNotRegionIcon,
              })}
            />
            <span ref={textRef}>{title}</span>
          </>
        )}
      </Checkbox>
    </CommonBubble>
  );
};

export default ExactOverflowMarketItem;
